import {graphql} from 'react-relay';
import type {Environment, MutationParameters, PayloadError} from 'relay-runtime';
import Util, {getValidationErrorMessageId} from '../../../util/util';
import {trackAndCommitMutation} from '../../../../../tracking/amplitude/TrackMutation';

export type ReassignPhaseSignoffApproverInput = {
	/** Relay global id of the phase (decoded server-side to the raw milestone id). */
	psaMilestoneId: string;
	/**
	 * The client workspace user to hand the OPEN request to — a portal_users UUID,
	 * not a Forecast person id and not a Relay id.
	 */
	approverPortalUserId: string;
	/**
	 * Why the request is being handed to someone else. Required server-side too, and
	 * lands in the audit trail; an empty string is rejected before this is called.
	 */
	reason: string;
};

const mutation = graphql`
	mutation reassignPhaseSignoffApproverMutation($input: ReassignPhaseSignoffApproverInput!) {
		reassignPhaseSignoffApprover(input: $input) {
			phase {
				id
				portalConfig {
					signoffRequestApproverPortalUserId
				}
			}
		}
	}
`;

const ReassignPhaseSignoffApproverMutation = {
	commit(
		environment: Environment,
		input: ReassignPhaseSignoffApproverInput,
		onSuccess: (response: unknown, errors: readonly PayloadError[] | null | undefined) => void,
		onError: (error: Error) => void
	) {
		return trackAndCommitMutation<MutationParameters>(environment, {
			mutation,
			variables: {
				input: {
					csrfToken: Util.getCsrfValue(),
					socketClientId: Util.getClientId(),
					...input,
				},
			},
			onCompleted: onSuccess,
			onError,
		} as never);
	},
};

/**
 * Hand a phase's OPEN sign-off request to a different client workspace user. Always via
 * Util.CommitMutation — never react-relay's commitMutation — so the correlation-id error
 * modal and the logged-out handling still apply.
 *
 * A recognised refusal is surfaced inline via the `onError` callback with a translation-id
 * string; anything else falls through to the framework's shared error modal. Inline is what
 * keeps the composed approver and reason alive — the shared warning modal would replace
 * this dialog, and the non-critical path renders behind it where it cannot be read.
 *
 * The refusals reuse the send and cancel copy rather than getting their own: a stale
 * approver list, a locked phase and a request that is no longer the live one read the same
 * whichever action ran into them, and a second set of strings saying the same thing is a
 * second set to keep translated.
 *
 * `retry=false` is deliberate, mirroring send and cancel: the write records an audit row.
 * A retried reassignment on a request that has meanwhile moved would arrive as exactly the
 * refusal this mapping exists to explain, so surface once and let the user re-press.
 *
 * Only the approver is selected back. A reassignment is not a new request — the deadline
 * and the requested-at moment stay put — so re-reading them would assert a change that
 * did not happen.
 */
export function reassignPhaseSignoffApprover(
	input: ReassignPhaseSignoffApproverInput,
	onSuccess: () => void,
	onError?: (validationMessageId: string | null) => void
): void {
	Util.CommitMutation(
		ReassignPhaseSignoffApproverMutation,
		input,
		onSuccess,
		false,
		null,
		(transaction: unknown, onFailure: (t: unknown) => unknown) => {
			const validationMessageId = getValidationErrorMessageId(String(transaction));
			if (validationMessageId) {
				if (onError) onError(validationMessageId);
				return;
			}
			const retryScheduled = onFailure(transaction);
			if (retryScheduled !== true && onError) onError(null);
		}
	);
}
